<template>
    <div class="impersonate-user-form">
        <!-- Barre de recherche avec bouton submit -->
        <div class="search-container">
            <input 
                v-model="query"
                type="text" 
                placeholder="Rechercher un utilisateur à voir en tant que"
                @keyup.enter="search"
                class="search-input"
            />
            <button 
                @click="search"
                :disabled="query.trim().length < 3 || loading"
                class="woocommerce-button button"
            >
                {{ loading ? 'Chargement...' : 'Rechercher' }}
            </button>
        </div>

        <!-- Combo box and view button (affichés uniquement lorsque des résultats existent et qu'il n'y a pas d'erreur) -->
        <div v-if="showCombo" class="combo-row-container">
            <select 
                v-model="selectedUserId"
                class="combo-box"
            >
                <option value="">-- Sélectionner un utilisateur --</option>
                <option 
                    v-for="user in results"
                    :key="user.userId"
                    :value="user.userId"
                >
                    {{ user.name }} ({{ user.email }})
                </option>
            </select>
            <a 
                v-if="showViewButton"
                :href="impersonateLink(selectedUserId)"
                class="woocommerce-button button alt"
            >
                Voir
            </a>
        </div>

        <!-- Message d'erreur (affiché uniquement lorsque l'erreur existe et que la combo est masquée) -->
        <div v-if="showError" class="error-message">
            {{ errorMessage }}
        </div>

        <div class="separator"></div>
    </div>
</template>

<script>

import api from '@/api/proxyWeb/utilisateurs'
import { createUrl } from '@/router'

export default {
    name: 'ImpersonateUserForm',

    data() {
        return {
            query: '',
            results: [],
            selectedUserId: '',
            loading: false,
            error: null,
            hasSearched: false
        }
    },

    computed: {
        showCombo() {
            return this.hasSearched && !this.error && this.results.length > 0
        },

        showViewButton() {
            return this.showCombo && this.selectedUserId
        },

        showError() {
            return this.hasSearched && this.error && !this.showCombo
        },

        errorMessage() {
            return this.error || 'Une erreur est survenue lors de la recherche.'
        }
    },

    methods: {
        async search() {
            if (!this.query.trim()) {
                return
            }

            this.loading = true
            this.error = null
            this.results = []
            this.selectedUserId = ''
            this.hasSearched = true

            try {
                const response = await api.getImpersonatables({ query: this.query })
                const data = response.data || response || []

                this.results = Array.isArray(data) ? data : (data.results || [])

                if (data.truncated) {
                    this.error = "Il y a trop d'utilisateurs correspondant à votre recherche, nous ne pouvons pas les afficher. Veuillez saisir plus de caractères."
                } else if (this.results.length === 0) {
                    this.error = 'Aucun utilisateur ne correspond à votre recherche.'
                } else if (this.results.length === 1) {
                    // Si un seul résultat est trouvé, le sélectionner automatiquement
                    this.selectedUserId = this.results[0].userId
                }
            } catch (err) {
                // Vérifie si l'erreur a une réponse et un statut différent de 200
                if (err.response && err.response.status !== 200) {
                    const errorData = err.response.data || {}
                    this.error = errorData.message || 'Erreur lors de la récupération des utilisateurs.'
                } else if (err.message) {
                    this.error = err.message
                } else {
                    this.error = 'Erreur lors de la connexion au serveur.'
                }
                this.results = []
            } finally {
                this.loading = false
            }
        },

        impersonateLink: function(userId) {
            return createUrl(this.$config.ACCOUNT_URL, { action: 'impersonate', user_id: userId })
        }
    }
}
</script>

<style scoped>
.impersonate-user-form {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    width: 100%;
}

.search-container {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
}

.search-input {
    flex: 1;
    min-width: 0;
    font-size: 140%;
    line-height: 160%;
    display: flex;
    align-items: center;
}

.combo-row-container {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
}

.combo-box {
    flex: 1;
    min-width: 0;
    line-height: 160%;
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 1.5rem;
}

.impersonate-user-form .woocommerce-button.button,
.impersonate-user-form .woocommerce-button.button.alt {
    flex-shrink: 0;
    min-width: 150px;
    margin-top: -0.25rem;
}

.error-message {
    padding: 0.75rem 1rem;
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    font-size: 0.95rem;
}

.separator {
    position: relative;
    width: 100%;
    border-bottom: 2px solid rgba(0,0,0,0.11);
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
}
</style>
